import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import DefaultEditor from 'react-simple-wysiwyg'
import { all_routes } from '../../router/all_routes'
import InstructorSidebar from '../common/instructorSidebar'
import { SubmitHandler, useForm } from 'react-hook-form'
import {
  AdditionalInformationFormValues,
  additionalInformationSchema,
  BasicInformationFormValues,
  basicInformationSchema,
  contentInformationFieldsConfig,
  CreateFormValues,
  initialFullFormValues,
  initialValuesStep1,
  initialValuesStep2,
  initialValuesStep3,
  pricingFieldsConfig,
  PricingFormValues,
  pricingSchema,
  transformPayload,
} from '../../../config/fields/cbt'
import { yupResolver } from '@hookform/resolvers/yup'
import DynamicSelectFetcher from '../../../core/common/DynamicSelectFetcher'
import { Select, Spin, TimePicker, Flex } from 'antd'
import dayjs from 'dayjs'
import { useCourseList } from '../courses/hooks/useCourses'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getCourseDetail, putCourseUpdate } from '../../../api/courses'
import { useNotify } from '../../../provider/NotificationProvider'
import AddQuestionForm from './components/QuestionForm'

const CBTEdit = () => {
  const route = all_routes
  const notify = useNotify()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const [currentStep, setCurrentStep] = useState(1)
  const [allFormData, setAllFormData] = useState<CreateFormValues>(
    initialFullFormValues,
  )
  const [studiedItems, setStudiedItems] = useState<string[]>([''])
  const [crossSellItems, setCrossSellItems] = useState<
    Array<{
      cross_course_id: number | null
      note: string
      price: number | null
    }>
  >([])

  // Track original image and poster values for comparison
  const [originalImageValues, setOriginalImageValues] = useState<{
    thumbnail?: string
    poster?: string
  }>({})

  const { data, isLoading } = useCourseList({
    initialLimit: 100,
    type: 'all',
  })

  const optionsCourse = data?.data?.map((course) => ({
    label: course.title,
    value: course.id,
  }))

  const { data: courseData, isLoading: isCourseDataLoading } = useQuery<any>({
    queryKey: ['course', id],
    queryFn: () => getCourseDetail(id || '', true),
    enabled: !!id,
  })

  const {
    register: registerStep1,
    handleSubmit: handleSubmitStep1,
    formState: { errors: errorsStep1 },
    setValue: setValueStep1,
    watch: watchStep1,
    trigger: triggerStep1,
    reset: resetStep1,
    getValues: getValuesStep1,
  } = useForm<BasicInformationFormValues>({
    resolver: yupResolver(basicInformationSchema),
    defaultValues: initialValuesStep1,
    mode: 'onBlur',
  })

  const {
    register: registerStep2,
    handleSubmit: handleSubmitStep2,
    formState: { errors: errorsStep2 },
    trigger: triggerStep2,
    getValues: getValuesStep2,
    setValue: setValueStep2,
    watch: watchStep2,
    reset: resetStep2,
  } = useForm<AdditionalInformationFormValues>({
    resolver: yupResolver(additionalInformationSchema),
    defaultValues: initialValuesStep2,
    mode: 'onBlur',
  })

  const {
    register: registerStep3,
    handleSubmit: handleSubmitStep3,
    formState: { errors: errorsStep3 },
    setValue: setValueStep3,
    watch: watchStep3,
    trigger: triggerStep3,
    reset: resetStep3,
    getValues: getValuesStep3,
  } = useForm<PricingFormValues>({
    resolver: yupResolver(pricingSchema),
    defaultValues: initialValuesStep3,
    mode: 'onBlur',
  })

  const updateMutation = useMutation({
    mutationFn: (data: any) => putCourseUpdate(id, data, true),
    onSuccess: () => {
      notify.success({
        message: 'Update berhasil',
        description: 'Data CBT berhasil diperbaharui',
      })
      navigate(route.adminCBT)
    },
    onError: (err: any) => {
      let errorMessage = 'Terjadi kesalahan.'

      // Extract error message from API response
      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err?.response?.data?.error) {
        errorMessage = err.response.data.error
      } else if (err?.response?.data) {
        // Handle validation errors like {"slug":["The slug has already been taken."]}
        if (
          typeof err.response.data === 'object' &&
          !Array.isArray(err.response.data)
        ) {
          const errorMessages: string[] = []

          Object.entries(err.response.data).forEach(([field, messages]) => {
            if (Array.isArray(messages)) {
              messages.forEach((message: string) => {
                errorMessages.push(`${field}: ${message}`)
              })
            } else if (typeof messages === 'string') {
              errorMessages.push(`${field}: ${messages}`)
            }
          })

          errorMessage =
            errorMessages.length > 0
              ? errorMessages.join('\n')
              : JSON.stringify(err.response.data)
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data
        }
      } else if (err?.message) {
        errorMessage = err.message
      }

      notify.error({
        message: 'Update CBT gagal',
        description: errorMessage,
      })
    },
  })

  const populateFormsFromFullCourseData = useCallback(
    (data: any) => {
      // --- STEP 1: Basic Information ---
      const basicInfoValues: Partial<BasicInformationFormValues> = {}
      Object.keys(initialValuesStep1).forEach((key) => {
        const apiValue = data[key]
        if (key === 'thumbnail') {
          basicInfoValues.thumbnail = data.image
        } else if (key === 'max_student') {
          basicInfoValues.max_student = String(apiValue)
        } else if (key === 'category_id') {
          basicInfoValues.category_id = String(apiValue)
        } else if (key === 'poster') {
          basicInfoValues.poster = data.poster
        } else if (key === 'duration') {
          // Convert duration from milliseconds to dayjs time object
          if (apiValue) {
            const totalMs = parseInt(apiValue)
            const totalMinutes = Math.floor(totalMs / (60 * 1000))
            const hours = Math.floor(totalMinutes / 60)
            const minutes = totalMinutes % 60
            basicInfoValues.duration = dayjs()
              .hour(hours)
              .minute(minutes)
              .toDate()
          }
        } else if (key in data) {
          basicInfoValues[key] = apiValue as any
        }
      })

      resetStep1(basicInfoValues as BasicInformationFormValues)
      setStudiedItems((basicInfoValues?.studied as any) || [])

      // Store original image values for comparison
      setOriginalImageValues({
        thumbnail: data.image,
        poster: data.poster,
      })

      // --- STEP 2: Additional Information ---
      const additionalInfoValues: any = {}
      Object.keys(initialValuesStep2).forEach((key) => {
        const apiValue = data[key]
        if (key === 'questions') {
          additionalInfoValues[key] = apiValue || []
        } else {
          additionalInfoValues[key] = apiValue as any
        }
      })
      resetStep2(additionalInfoValues)

      // --- STEP 3: Pricing ---
      const pricingValues: any = {}
      Object.keys(initialValuesStep3).forEach((key) => {
        const apiValue = data[key]

        if (key === 'price' || key === 'discount') {
          pricingValues[key as 'price' | 'discount'] = parseFloat(
            apiValue as string,
          )
        } else if (key === 'cross_sell') {
          pricingValues.cross_sell =
            data.cross_sells?.map((apiCrossSell: any) => ({
              ...apiCrossSell,
              cross_course_id: parseInt(apiCrossSell.cross_course_id),
            })) || []
        } else if (key === 'is_paid') {
          pricingValues.is_paid = parseFloat(data.price) > 0
        } else if (key in data) {
          pricingValues[key as keyof PricingFormValues] = apiValue as any
        }
      })
      resetStep3(pricingValues as PricingFormValues)

      setCrossSellItems(pricingValues?.cross_sell || [])
    },
    [resetStep1, resetStep2, resetStep3],
  )

  useEffect(() => {
    if (courseData) {
      populateFormsFromFullCourseData(courseData)
    }
  }, [courseData, populateFormsFromFullCourseData])

  useEffect(() => {
    if (studiedItems.length > 0) {
      setValueStep1('studied', studiedItems, {
        shouldValidate: true,
        shouldDirty: true,
      })
    } else {
      setValueStep1('studied', null, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }, [studiedItems, setValueStep1])

  const addNewStudiedItem = () => {
    setStudiedItems([...studiedItems, ''])
  }

  const updateStudiedItem = (index: number, value: number | string) => {
    const newItems: any = [...studiedItems]
    newItems[index] = value
    setStudiedItems(newItems)
  }

  const removeStudiedItem = (index: number) => {
    setStudiedItems(studiedItems.filter((_, i) => i !== index))
  }

  useEffect(() => {
    if (crossSellItems.length > 0) {
      setValueStep3('cross_sell', crossSellItems, {
        shouldValidate: true,
        shouldDirty: true,
      })
    } else {
      setValueStep3('cross_sell', null, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }, [crossSellItems, setValueStep3])

  const addNewCrossSellItem = () => {
    setCrossSellItems([
      ...crossSellItems,
      { cross_course_id: null, note: '', price: null },
    ])
  }

  const updateCrossSellItem = (
    index: number,
    field: 'cross_course_id' | 'note' | 'price',
    value: number | string,
  ) => {
    const newItems: any = [...crossSellItems]
    newItems[index][field] = value
    setCrossSellItems(newItems)
  }

  const removeCrossSellItem = (index: number) => {
    setCrossSellItems(crossSellItems.filter((_, i) => i !== index))
  }

  const handleNextStep = async () => {
    let isValid = false
    let currentStepData: any = null

    if (currentStep === 1) {
      isValid = await triggerStep1()
      if (isValid) {
        currentStepData = getValuesStep1()
        setAllFormData((prev) => ({ ...prev, ...currentStepData }))
      }
    } else if (currentStep === 2) {
      isValid = await triggerStep2()
      if (isValid) {
        currentStepData = getValuesStep2()
        setAllFormData((prev) => ({ ...prev, ...currentStepData }))
      }
    }

    if (isValid) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleSelectChange = (
    fieldName: keyof BasicInformationFormValues | keyof PricingFormValues,
    value: any,
    setFormValue: any,
  ) => {
    setFormValue(fieldName, value)
  }

  const handleEditorChange = (
    fieldName: keyof BasicInformationFormValues,
    value: string,
    setFormValue: any,
  ) => {
    setFormValue(fieldName, value, { shouldValidate: true, shouldDirty: true })
  }

  const handleDatePickerChange = (
    key: string,
    date: dayjs.Dayjs | null,
    setValueFn: typeof setValueStep1,
  ) => {
    setValueFn(key, date ? date.toDate() : null, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1)
  }

  const onSubmitFinal: SubmitHandler<any> = async () => {
    const isStepValid = await triggerStep3()

    if (!isStepValid) {
      return
    }

    // Update allFormData with the last step's data before constructing payload
    const finalStepData = getValuesStep3()

    const finalAllFormData = { ...allFormData, ...finalStepData }
    const dataFormatted = transformPayload(
      finalAllFormData,
      originalImageValues,
    )
    console.log('recon sini aja', finalAllFormData, dataFormatted)
    updateMutation.mutate(dataFormatted)
  }

  return (
    <>
      <div className="content">
        <div className="container">
          <div className="row">
            <InstructorSidebar />
            <div className="col-lg-9 mx-auto">
              <div className="add-course-item">
                <div className="wizard">
                  <ul className="form-wizard-steps" id="progressbar2">
                    {[1, 2, 3].map((stepNum) => (
                      <li
                        key={stepNum}
                        className={
                          currentStep === stepNum
                            ? 'progress-active'
                            : currentStep > stepNum
                            ? 'progress-activated'
                            : ''
                        }
                      >
                        <div className="profile-step">
                          <span className="dot-active mb-2">
                            <span className="number">0{stepNum}</span>
                            <span className="tickmark">
                              <i className="fa-solid fa-check" />
                            </span>
                          </span>
                          <div className="step-section">
                            <p>
                              {stepNum === 1 && 'Content Information'}
                              {stepNum === 2 && 'Question & Answer'}
                              {stepNum === 3 && 'Pricing'}
                            </p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                {isCourseDataLoading ? (
                  <Flex className="h-100 mt-5" align="center" justify="center">
                    <Spin tip="Loading..." size="large" />
                  </Flex>
                ) : (
                  <div className="initialization-form-set">
                    {currentStep === 1 && (
                      <form
                        onSubmit={handleSubmitStep1(handleNextStep)}
                        className="form-inner wizard-form-card"
                        id="first"
                      >
                        <div className="title">
                          <h5>Basic Information</h5>
                        </div>
                        <div className="row">
                          {Object.entries(contentInformationFieldsConfig).map(
                            ([key, fieldConfig]: any) => {
                              const isDynamicSelect =
                                fieldConfig.type === 'select' &&
                                fieldConfig.isSearchableAndInfinite
                              const isStaticSelect =
                                fieldConfig.type === 'select' &&
                                !fieldConfig.isSearchableAndInfinite

                              const error = errorsStep1[key]

                              const inputClassName = `form-control ${
                                error ? 'is-invalid' : ''
                              }`
                              const isFullWidth = [
                                'title',
                                'short_description',
                                'description',
                                'thumbnail',
                                'poster',
                                'duration',
                                'studied',
                              ].includes(key)

                              const isThreeColumn = [
                                'category_id',
                                'course_level',
                                'max_student',
                              ].includes(key)

                              return (
                                <div
                                  className={`col-md-${
                                    isFullWidth
                                      ? '12'
                                      : isThreeColumn
                                      ? '4'
                                      : '6'
                                  }`}
                                  key={key}
                                >
                                  <div className="input-block">
                                    <label className="form-label">
                                      {fieldConfig.label}
                                      {fieldConfig.required && (
                                        <span className="text-danger ms-1">
                                          *
                                        </span>
                                      )}
                                    </label>
                                    {(fieldConfig.type === 'text' ||
                                      fieldConfig.type === 'number' ||
                                      fieldConfig.type === 'url') && (
                                      <input
                                        type={
                                          fieldConfig.type === 'url'
                                            ? 'text'
                                            : fieldConfig.type
                                        }
                                        {...registerStep1(key, {
                                          valueAsNumber:
                                            fieldConfig.type === 'number',
                                        })}
                                        className={inputClassName}
                                        placeholder={fieldConfig.placeholder}
                                      />
                                    )}
                                    {fieldConfig.type === 'time' && (
                                      <TimePicker
                                        {...registerStep1(key)}
                                        ref={registerStep1(key).ref} // Penting untuk meneruskan ref
                                        className={`${inputClassName} w-100`} // Terapkan styling
                                        placeholder={fieldConfig.placeholder}
                                        value={
                                          watchStep1(key)
                                            ? dayjs(
                                                watchStep1(key) as any,
                                                // "HH:mm"
                                              )
                                            : null
                                        }
                                        onChange={(date, dateString) =>
                                          handleDatePickerChange(
                                            key,
                                            date,
                                            setValueStep1,
                                          )
                                        }
                                        format="HH:mm"
                                        status={error ? 'error' : undefined}
                                      />
                                    )}

                                    {fieldConfig.type === 'textarea' && (
                                      <textarea
                                        {...registerStep1(key)}
                                        className={inputClassName}
                                        placeholder={fieldConfig.placeholder}
                                      ></textarea>
                                    )}

                                    {isDynamicSelect && (
                                      <DynamicSelectFetcher
                                        {...registerStep1(key)}
                                        fieldConfig={fieldConfig}
                                        value={watchStep1(key)}
                                        onChange={(selectedOption: any) => {
                                          handleSelectChange(
                                            key,
                                            selectedOption,
                                            setValueStep1,
                                          )
                                        }}
                                        placeholder={fieldConfig.placeholder}
                                      />
                                    )}
                                    {isStaticSelect && (
                                      <Select
                                        {...registerStep1(key)}
                                        options={fieldConfig?.options || []}
                                        className="select d-flex"
                                        style={{ width: '100%' }}
                                        placeholder={fieldConfig.placeholder}
                                        value={
                                          fieldConfig?.options?.find(
                                            (opt: any) =>
                                              opt.value === watchStep1(key),
                                          ) || null
                                        }
                                        onChange={(selectedOption: any) => {
                                          handleSelectChange(
                                            key,
                                            selectedOption,
                                            setValueStep1,
                                          )
                                        }}
                                      />
                                    )}
                                    {fieldConfig.type === 'richtext' && (
                                      <div className="summernote">
                                        <DefaultEditor
                                          value={
                                            (watchStep1(key) as string) || ''
                                          }
                                          onChange={(e) =>
                                            handleEditorChange(
                                              key,
                                              e.target.value,
                                              setValueStep1,
                                            )
                                          }
                                        />
                                      </div>
                                    )}
                                    {fieldConfig.type === 'array' && (
                                      <>
                                        {studiedItems.length === 0 && (
                                          <div className="alert alert-info text-center">
                                            No Data added.
                                          </div>
                                        )}
                                        {studiedItems.map((item, index) => {
                                          return (
                                            <div
                                              key={index}
                                              className="d-flex align-items-center mb-2"
                                            >
                                              <div className="flex-grow-1 me-2">
                                                <input
                                                  type="text"
                                                  className="form-control w-100"
                                                  placeholder="(e.g., Studi kasus terbaru)"
                                                  value={item}
                                                  onChange={(e) =>
                                                    updateStudiedItem(
                                                      index,
                                                      e.target.value,
                                                    )
                                                  }
                                                />
                                              </div>
                                              <Link
                                                to="#"
                                                className="link-trash delete-btn1"
                                                onClick={() =>
                                                  removeStudiedItem(index)
                                                }
                                              >
                                                <i className="isax isax-trash fs-16" />
                                              </Link>
                                            </div>
                                          )
                                        })}

                                        <div className="d-flex align-items-center justify-content-end">
                                          <Link
                                            to="#"
                                            className="d-flex align-items-center add-new-topic"
                                            id="add-new-topic-btn"
                                            onClick={addNewStudiedItem}
                                          >
                                            <i className="isax isax-add me-1" />{' '}
                                            Add New Data
                                          </Link>
                                          <div></div>
                                        </div>
                                      </>
                                    )}
                                    {fieldConfig.type === 'file' && (
                                      <div className="row align-items-center">
                                        {watchStep1(key) &&
                                          typeof watchStep1(key) ===
                                            'string' && (
                                            <div>
                                              <img
                                                src={`${
                                                  process.env
                                                    .REACT_APP_BASE_URL_API
                                                }/public/storage/${watchStep1(
                                                  key,
                                                )}`}
                                                alt="Current Thumbnail"
                                                style={{
                                                  maxWidth: '200px',
                                                  maxHeight: '200px',
                                                  marginTop: '10px',
                                                }}
                                              />
                                              <p>Gambar Saat Ini</p>
                                            </div>
                                          )}
                                        {typeof watchStep1(key) !== 'string' &&
                                          !!watchStep1(key) && (
                                            <div>
                                              <img
                                                src={URL.createObjectURL(
                                                  watchStep1(key)?.[0],
                                                )}
                                                alt="New Thumbnail Preview"
                                                style={{
                                                  maxWidth: '200px',
                                                  maxHeight: '200px',
                                                  marginTop: '10px',
                                                }}
                                              />
                                              <p>Pratinjau Gambar Baru</p>
                                            </div>
                                          )}
                                        <div className="col-md-10">
                                          <input
                                            type="text"
                                            className={inputClassName}
                                            placeholder="No File Selected"
                                            readOnly
                                            value={
                                              watchStep1(key)?.[0]?.name ||
                                              watchStep1(key) ||
                                              ''
                                            }
                                          />
                                        </div>
                                        <div className="col-md-2 d-grid">
                                          <label
                                            htmlFor={`${key}-upload`}
                                            className="file-upload-btn text-center"
                                          >
                                            Upload File
                                          </label>
                                          <input
                                            type="file"
                                            id={`${key}-upload`}
                                            {...registerStep1(key)}
                                            className="d-none" // Hide default file input
                                          />
                                        </div>
                                      </div>
                                    )}
                                    {error && (
                                      <div className="text-danger">
                                        {error.message}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )
                            },
                          )}
                        </div>
                        <div className="add-form-btn widget-next-btn submit-btn d-flex justify-content-end mb-0">
                          <div className="btn-left">
                            <button
                              type="submit"
                              className="btn main-btn next_btns"
                            >
                              Next{' '}
                              <i className="isax isax-arrow-right-3 ms-1" />
                            </button>
                          </div>
                        </div>
                      </form>
                    )}
                    {currentStep === 2 && (
                      <form
                        onSubmit={handleSubmitStep2(handleNextStep)} // Gunakan handleSubmitStep2 langsung
                        className="form-inner wizard-form-card"
                        id="second"
                      >
                        <div className="row">
                          <div className="col-md-12">
                            <AddQuestionForm
                              name="questions"
                              register={registerStep2 as any}
                              errors={errorsStep2 as any}
                              setValue={setValueStep2 as any}
                              watch={watchStep2 as any}
                            />
                            {errorsStep2.questions && (
                              <div className="text-danger mt-2">
                                {errorsStep2.questions.message as string}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="add-form-btn widget-next-btn submit-btn">
                          <div className="btn-left">
                            <Link
                              to="#"
                              className="btn btn-light main-btn prev_btns"
                              onClick={handlePrevStep}
                            >
                              <i className="isax isax-arrow-left-2 me-1" />
                              Prev
                            </Link>
                          </div>
                          <div className="btn-left">
                            <button
                              type="submit"
                              className="btn main-btn next_btns"
                            >
                              Next{' '}
                              <i className="isax isax-arrow-right-3 ms-1" />
                            </button>
                          </div>
                        </div>
                      </form>
                    )}

                    {currentStep === 3 && (
                      <form
                        onSubmit={handleSubmitStep3(onSubmitFinal)}
                        className="form-inner wizard-form-card"
                        style={{ display: 'block' }}
                      >
                        <div className="title">
                          <h5>Pricing</h5>
                        </div>
                        <div className="row">
                          {Object.entries(pricingFieldsConfig).map(
                            ([key, fieldConfig]: any) => {
                              const error = errorsStep3[key]
                              const inputClassName = `form-control ${
                                error ? 'is-invalid' : ''
                              }`

                              const isDiscountField =
                                key === 'discount_type' || key === 'discount'
                              const isDisabled =
                                isDiscountField && watchStep3('price') === 0

                              return (
                                <div className="col-md-12" key={key}>
                                  <div className="input-block">
                                    <label className="form-label">
                                      {fieldConfig.label}
                                      {fieldConfig.required && (
                                        <span className="text-danger ms-1">
                                          *
                                        </span>
                                      )}
                                    </label>
                                    {(fieldConfig.type === 'text' ||
                                      fieldConfig.type === 'number') && (
                                      <input
                                        type={fieldConfig.type}
                                        {...registerStep3(key, {
                                          valueAsNumber:
                                            fieldConfig.type === 'number',
                                        })}
                                        className={inputClassName}
                                        placeholder={fieldConfig.placeholder}
                                        disabled={isDisabled}
                                      />
                                    )}
                                    {fieldConfig.type === 'select' && (
                                      <Select
                                        {...registerStep3(key)}
                                        options={fieldConfig?.options || []}
                                        className="select d-flex"
                                        placeholder={fieldConfig.placeholder}
                                        value={
                                          fieldConfig.options?.find(
                                            (opt: any) =>
                                              opt.value === watchStep3(key),
                                          ) || null
                                        }
                                        onChange={(selectedOption: any) =>
                                          handleSelectChange(
                                            key,
                                            selectedOption,
                                            setValueStep3,
                                          )
                                        }
                                        disabled={isDisabled}
                                      />
                                    )}

                                    {fieldConfig.type === 'array' && (
                                      <>
                                        {crossSellItems.length === 0 && (
                                          <div className="alert alert-info text-center">
                                            No cross-sell products added.
                                          </div>
                                        )}
                                        {crossSellItems.map((item, index) => {
                                          const data: any = watchStep3?.(key)?.[
                                            index
                                          ]

                                          const value =
                                            optionsCourse?.find(
                                              (opt: any) =>
                                                opt.value?.toString() ===
                                                data?.cross_course_id?.toString(),
                                            ) || null
                                          return (
                                            <div
                                              key={index}
                                              className="d-flex align-items-center mb-2"
                                            >
                                              <div
                                                className="flex-grow-1 me-2"
                                                style={{ maxWidth: '30vw' }}
                                              >
                                                <Select
                                                  {...registerStep3(key)}
                                                  options={optionsCourse || []}
                                                  className="select w-100"
                                                  placeholder={
                                                    fieldConfig.placeholder
                                                  }
                                                  loading={isLoading}
                                                  value={value}
                                                  onChange={(
                                                    selectedOption: any,
                                                  ) =>
                                                    updateCrossSellItem(
                                                      index,
                                                      'cross_course_id',
                                                      selectedOption,
                                                    )
                                                  }
                                                />
                                              </div>
                                              <div className="flex-grow-1 me-2">
                                                <input
                                                  type="text"
                                                  className="form-control w-100"
                                                  placeholder="Note (e.g., discount for this product)"
                                                  value={item.note}
                                                  onChange={(e) =>
                                                    updateCrossSellItem(
                                                      index,
                                                      'note',
                                                      e.target.value,
                                                    )
                                                  }
                                                />
                                              </div>
                                              <div className="flex-grow-1 me-2">
                                                <input
                                                  type="number"
                                                  className="form-control w-100"
                                                  placeholder="Price *"
                                                  value={item.price || ''}
                                                  onChange={(e) =>
                                                    updateCrossSellItem(
                                                      index,
                                                      'price',
                                                      e.target.value === ''
                                                        ? null
                                                        : Number(
                                                            e.target.value,
                                                          ),
                                                    )
                                                  }
                                                  required
                                                />
                                              </div>
                                              <Link
                                                to="#"
                                                className="link-trash delete-btn1"
                                                onClick={() =>
                                                  removeCrossSellItem(index)
                                                }
                                              >
                                                <i className="isax isax-trash fs-16" />
                                              </Link>
                                            </div>
                                          )
                                        })}

                                        <div className="d-flex align-items-center justify-content-end">
                                          <Link
                                            to="#"
                                            className="d-flex align-items-center add-new-topic"
                                            id="add-new-topic-btn"
                                            onClick={addNewCrossSellItem}
                                          >
                                            <i className="isax isax-add me-1" />{' '}
                                            Add New Cross-Sell Product
                                          </Link>
                                          <div></div>
                                        </div>
                                      </>
                                    )}
                                    {error && (
                                      <div className="text-danger">
                                        {error.message}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )
                            },
                          )}
                        </div>
                        <div className="add-form-btn widget-next-btn submit-btn">
                          <div className="btn-left">
                            <button
                              type="button"
                              className="btn btn-light main-btn prev_btns"
                              onClick={handlePrevStep}
                              disabled={updateMutation.isPending}
                            >
                              <i className="isax isax-arrow-left-2 me-1" /> Prev
                            </button>
                          </div>
                          <div className="btn-left">
                            <button
                              type="submit"
                              className="btn btn-success main-btn"
                              disabled={updateMutation.isPending}
                            >
                              Update CBT
                            </button>
                          </div>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default CBTEdit
